import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calculator, AlertTriangle, ShieldCheck, Banknote, CreditCard, Globe, ArrowLeftRight, Zap, HelpCircle, Upload } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/utils/motionVariants";

const ALL_TOOLS = {
  "/switch-and-save": { title: "Savings Calculator", desc: "Compare your current costs with CanDo pricing.", icon: Calculator },
  "/cost-of-cash": { title: "Cost of Cash Calculator", desc: "See the real cost of handling cash.", icon: Banknote },
  "/cash-vs-card": { title: "Cash vs Card Comparator", desc: "Side-by-side cash vs card comparison.", icon: ArrowLeftRight },
  "/quote/in-person": { title: "In-Person Quote", desc: "Estimate your in-person processing costs.", icon: CreditCard },
  "/quote/online": { title: "Online Quote", desc: "Estimate your online processing costs.", icon: Globe },
  "/overpaying-scorecard": { title: "Overpaying Scorecard", desc: "Score your rates against benchmarks.", icon: AlertTriangle },
  "/hidden-fees-checker": { title: "Hidden Fees Checker", desc: "Check if your fees look high.", icon: ShieldCheck },
  "/switch-vs-stay": { title: "Switch vs Stay", desc: "See if switching would pay off.", icon: Zap },
  "/statement-review": { title: "Statement Review", desc: "Upload your statement for expert review.", icon: Upload },
  "/payment-finder": { title: "Payment Solution Finder", desc: "Take our quiz to find the right solution.", icon: HelpCircle },
};

const RELATED_MAP = {
  "/switch-and-save": ["/overpaying-scorecard", "/hidden-fees-checker", "/statement-review"],
  "/cost-of-cash": ["/cash-vs-card", "/switch-and-save", "/quote/in-person"],
  "/cash-vs-card": ["/switch-and-save", "/cost-of-cash", "/quote/in-person"],
  "/quote/in-person": ["/switch-and-save", "/overpaying-scorecard", "/cost-of-cash"],
  "/quote/online": ["/switch-and-save", "/hidden-fees-checker", "/payment-finder"],
  "/overpaying-scorecard": ["/hidden-fees-checker", "/switch-and-save", "/statement-review"],
  "/hidden-fees-checker": ["/overpaying-scorecard", "/statement-review", "/switch-vs-stay"],
  "/switch-vs-stay": ["/switch-and-save", "/overpaying-scorecard", "/hidden-fees-checker"],
  "/statement-review": ["/overpaying-scorecard", "/hidden-fees-checker", "/switch-and-save"],
  "/payment-finder": ["/switch-and-save", "/quote/in-person", "/overpaying-scorecard"],
  "/epos-partner-calculator": ["/ecom-partner-calculator", "/payment-finder", "/switch-and-save"],
  "/ecom-partner-calculator": ["/epos-partner-calculator", "/payment-finder", "/quote/online"],
};

export default function RelatedTools({ currentPath }) {
  const related = RELATED_MAP[currentPath];
  if (!related) return null;

  const tools = related.map((path) => ({ path, ...ALL_TOOLS[path] })).filter((t) => t.title);
  if (tools.length === 0) return null;

  return (
    <section className="py-14 bg-gray-50" data-testid="related-tools">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-2">Keep exploring</p>
            <h3 className="font-heading text-2xl font-bold text-brand-black tracking-tight">You might also find these useful</h3>
          </motion.div>
          <motion.div variants={stagger} className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
            {tools.map((tool) => {
              const Icon = tool.icon;
              return (
                <motion.div key={tool.path} variants={fadeUp}>
                  <Link to={tool.path} className="group block bg-white border border-brand-light-grey rounded-2xl p-5 hover:border-brand-aqua hover:shadow-md hover:-translate-y-0.5 transition-all duration-300" data-testid={`related-${tool.path.replace(/\//g, "-").slice(1)}`}>
                    <div className="w-9 h-9 rounded-lg bg-brand-aqua-light flex items-center justify-center mb-3 group-hover:bg-brand-aqua transition-colors">
                      <Icon size={18} className="text-brand-aqua group-hover:text-white transition-colors" />
                    </div>
                    <h4 className="font-heading text-sm font-bold text-brand-black mb-1">{tool.title}</h4>
                    <p className="text-xs text-brand-grey leading-relaxed">{tool.desc}</p>
                    <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-aqua group-hover:gap-2 transition-all">Try it <ArrowRight size={12} /></span>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
